import client from "@/utils/api/client";
import DefaultLayout from "@/layouts/DefaultLayout";
import publicPath from "@/utils/publicPath";
import { TeamDetailResponse } from "@/web-client/api";
import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import { NextSeo } from "next-seo";
import { notFoundWrapper } from "@/utils/notFoundWrapper";
import { useMemo } from "react";
import { TabProps } from "@/components/Tab";
import Tabs from "@/components/Tabs";
import ProjectItemList from "@/features/project/components/ProjectItemList";
import ProjectItem from "@/features/project/components/ProjectItem";
import TeamGrid from "@/features/team/components/TeamGrid";
import useHasCompleteParameter from "@/hooks/useHasCompleteParameter";
import RenderIfAuth from "@/components/RenderIfAuth";
import useDisclosure from "@/hooks/useDisclosure";
import ProjectItemsForNotFound from "@/features/project/components/ProjectItemsForNotFound";
import InfiniteScroll from "react-infinite-scroll-component";
import Circular from "@/components/Circular";
import ProjectItemForCreate from "@/features/project/components/ProjectItemForCreate";
import useIsMyTeam from "@/features/team/hooks/useIsMyTeam";
import dynamic from "next/dynamic";
import usePageView from "@/hooks/usePageView";
import { useGetTeamProjects } from "@/features/project/api/getTeamProjects";

const DynamicDialogCompleteCreatingTeam = dynamic(
  () => import("@/features/team/components/DialogCompleteCreatingTeam"),
);

type Props = {
  team: TeamDetailResponse;
};

const TeamProjectsPage: NextPage<Props> = ({ team }): JSX.Element => {
  usePageView();

  const {
    data: projects,
    notFound,
    hasNext,
    dataLength,
    next,
    updateItem,
    removeItem,
  } = useGetTeamProjects({ id: team.id, limit: 20 });

  const hasCompletion = useHasCompleteParameter();
  const { isOpen: isCompletionOpen } = useDisclosure(hasCompletion);
  const isMyTeam = useIsMyTeam(team.id, { skipCheckDesigner: true });

  const tabs = useMemo<TabProps[]>(
    () => [
      {
        isCurrent: true,
        to: {
          pathname: `/teams/${team.id}/projects`,
        },
        label: "プロジェクト",
      },
      {
        isCurrent: false,
        to: {
          pathname: `/teams/${team.id}/members`,
        },
        label: "メンバー",
      },
    ],
    [team],
  );

  return (
    <DefaultLayout>
      <NextSeo
        title={team.name}
        description={`${team.name} | ${team.profile}`}
        canonical={publicPath(`/teams/${team.id}/projects`)}
      />
      <TeamGrid team={team}>
        <main className="space-y-24">
          <Tabs tabs={tabs} />

          {notFound && <ProjectItemsForNotFound enablePost={isMyTeam} />}
          {!notFound && (
            <InfiniteScroll
              dataLength={dataLength}
              hasMore={hasNext}
              next={next}
              loader={<Circular />}
            >
              <ProjectItemList projects={projects}>
                {isMyTeam && (
                  <li className="[&>*]:w-full [&>*]:h-full">
                    <ProjectItemForCreate />
                  </li>
                )}
                {projects.map((project) => {
                  return (
                    <li key={project.id}>
                      <ProjectItem
                        hasTeam={false}
                        project={project}
                        mutate={(updatedProject) =>
                          updateItem(updatedProject, false)
                        }
                        removeMutation={() => removeItem(project)}
                        showMenuBtn
                      />
                    </li>
                  );
                })}
              </ProjectItemList>
            </InfiniteScroll>
          )}
        </main>
      </TeamGrid>

      <RenderIfAuth>
        <DynamicDialogCompleteCreatingTeam isOpen={isCompletionOpen} />
      </RenderIfAuth>
    </DefaultLayout>
  );
};

export default TeamProjectsPage;

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  return notFoundWrapper<Props>(async () => {
    const { data: team } = await client.teamsIdGet({ id: Number(params.id) });

    return {
      props: { team },
      revalidate: 1,
    };
  });
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: "blocking",
});
